// The running cash position over the calendar, as a step line: flat between cash events, a jump at
// each. Below zero is shaded - that area is what the trade has to be funded through - and the
// facility limit is drawn when the funding comes anywhere near it.
//
// Drawn in SVG here rather than through a chart package: one line, two rules and a marker are not
// worth a dependency. Hovering a point names the events that moved the balance that day.

import { useEffect, useRef, useState } from 'react';

import { day, millions, money } from './format.js';
import { addDays } from './inputs.js';

const HEIGHT = 280;
const MARGIN = { top: 16, right: 16, bottom: 28, left: 64 };
const Y_TICKS = 5;
const X_TICK_DAYS = 30;
// The facility line joins the scale only when funding reaches this share of it; a limit far above
// the need would flatten the line it is there to judge.
const FACILITY_IN_VIEW = 1 / 3;

function useWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

function niceStep(span) {
  const raw = span / Y_TICKS;
  const power = 10 ** Math.floor(Math.log10(raw));
  const unit = [1, 2, 2.5, 5, 10].find((m) => m * power >= raw);
  return unit * power;
}

// One point per day on which the balance moved: the day's closing balance and what moved it.
function closingBalances(events) {
  const days = [];
  for (const event of events) {
    const last = days[days.length - 1];
    if (last && last.day === event.day) {
      last.balance = Number(event.balance);
      last.labels.push(`${event.label} ${money(event.amount)}`);
    } else {
      days.push({ day: event.day, date: event.date, balance: Number(event.balance),
                  labels: [`${event.label} ${money(event.amount)}`] });
    }
  }
  return days;
}

// The spacing of the hatch on the area below zero, in pixels.
const HATCH = 7;

export default function CashChart({ result, facilityLimit }) {
  const [ref, width] = useWidth();
  const points = closingBalances(result.events);
  const peak = Number(result.metrics.peak_exposure);
  const facility = Number(facilityLimit);
  const showFacility = peak > 0 && facility > 0 && peak >= facility * FACILITY_IN_VIEW;

  const plot = { w: Math.max(width - MARGIN.left - MARGIN.right, 1), h: HEIGHT - MARGIN.top - MARGIN.bottom };
  const lastDay = Math.max(points[points.length - 1]?.day ?? 1, 1);
  const firstDay = Math.min(points[0]?.day ?? 0, 0);
  const values = [0, ...points.map((p) => p.balance), ...(showFacility ? [-facility] : [])];
  const step = niceStep((Math.max(...values) - Math.min(...values)) || 1);
  const lo = Math.floor(Math.min(...values) / step) * step;
  const hi = Math.ceil(Math.max(...values) / step) * step || step;

  const x = (d) => MARGIN.left + ((d - firstDay) / (lastDay - firstDay || 1)) * plot.w;
  const y = (v) => MARGIN.top + (1 - (v - lo) / (hi - lo)) * plot.h;

  let path = `M${x(firstDay)},${y(0)}`;
  let balance = 0;
  for (const p of points) {
    path += ` H${x(p.day)} V${y(p.balance)}`;
    balance = p.balance;
  }
  path += ` H${x(lastDay)}`;
  const area = `${path} V${y(0)} Z`;

  const yTicks = [];
  for (let v = lo; v <= hi + step / 2; v += step) yTicks.push(v);
  const xTicks = [];
  for (let d = 0; d <= lastDay; d += X_TICK_DAYS) xTicks.push(d);
  // Calendar arithmetic in UTC, so the tick names the same day whatever the viewer's timezone.
  // Day 0 is the result's own: every event carries its date and its day number.
  const origin = addDays(result.events[0].date, -result.events[0].day);
  const dateOf = (d) => day(addDays(origin, d));
  const lowest = points.reduce((a, p) => (p.balance < a.balance ? p : a), { balance: 0 });

  return (
    <div className="econ-chart" ref={ref}>
      {width > 0 && (
        <svg width={width} height={HEIGHT} role="img"
             aria-label={`Running cash position; final ${money(balance)}`}>
          <defs>
            <clipPath id="econ-below-zero">
              <rect x={0} y={y(0)} width={width} height={HEIGHT} />
            </clipPath>
            {/* Below zero the trade is borrowing: hatched in the error ink. */}
            <pattern id="econ-hatch" width={HATCH} height={HATCH} patternUnits="userSpaceOnUse"
                     patternTransform="rotate(45)">
              <rect width={HATCH} height={HATCH} className="econ-hatch-ground" />
              <line x1={0} y1={0} x2={0} y2={HATCH} className="econ-hatch-stroke" />
            </pattern>
          </defs>
          {yTicks.map((v) => (
            <g key={v}>
              <line x1={MARGIN.left} x2={width - MARGIN.right} y1={y(v)} y2={y(v)}
                    className={v === 0 ? 'econ-axis-zero' : 'econ-grid'} />
              <text x={MARGIN.left - 8} y={y(v) + 4} textAnchor="end">{millions(v)}</text>
            </g>
          ))}
          {xTicks.map((d) => (
            <text key={d} x={x(d)} y={HEIGHT - 8} textAnchor="middle">{dateOf(d)}</text>
          ))}
          <path d={area} className="econ-area-negative" clipPath="url(#econ-below-zero)" />
          {showFacility && (
            <g>
              <line x1={MARGIN.left} x2={width - MARGIN.right} y1={y(-facility)} y2={y(-facility)}
                    className="econ-facility" />
              <text x={width - MARGIN.right} y={y(-facility) - 6} textAnchor="end"
                    className="econ-facility-label">Facility {millions(facility)}</text>
            </g>
          )}
          <path d={path} className="econ-line" />
          {points.map((p) => (
            <circle key={p.day} cx={x(p.day)} cy={y(p.balance)} r={3.5} className="econ-point">
              <title>{`${day(p.date)} · balance ${money(p.balance)}\n${p.labels.join('\n')}`}</title>
            </circle>
          ))}
          {lowest.balance < 0 && (
            <circle cx={x(lowest.day)} cy={y(lowest.balance)} r={8} className="econ-peak" />
          )}
        </svg>
      )}
    </div>
  );
}
